import { useCallback, useEffect, useRef, useState, type FocusEvent, type KeyboardEvent } from "react";

export const MEGA_MENU_PANEL_WIDTH = 1320;

const CLOSE_DELAY_MS = 200;
const PANEL_OFFSET_TOP = 4;

interface UseHoverDropdownOptions {
    panelWidth?: number;
}

export function useHoverDropdown({ panelWidth = MEGA_MENU_PANEL_WIDTH }: UseHoverDropdownOptions = {}) {
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const anchorRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);

    const close = useCallback(() => setOpen(false), []);

    const openMenu = useCallback(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        if (anchorRef.current) {
            const rect = anchorRef.current.getBoundingClientRect();
            const centeredLeft = rect.left + rect.width / 2 - panelWidth / 2;
            const left = Math.max(8, Math.min(centeredLeft, window.innerWidth - panelWidth - 8));
            setPosition({ top: rect.bottom + PANEL_OFFSET_TOP, left });
        }
        setOpen(true);
    }, [panelWidth]);

    const scheduleClose = useCallback(() => {
        timerRef.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
    }, []);

    const containsFocusTarget = useCallback((target: EventTarget | null) => {
        if (!(target instanceof Node)) return false;
        return Boolean(anchorRef.current?.contains(target) || panelRef.current?.contains(target));
    }, []);

    const handleBlur = useCallback(
        (event: FocusEvent) => {
            if (containsFocusTarget(event.relatedTarget)) return;
            scheduleClose();
        },
        [containsFocusTarget, scheduleClose],
    );

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                close();
                // Return focus to the anchor trigger so keyboard context is preserved
                (anchorRef.current?.querySelector("a, button") as HTMLElement | null)?.focus();
            }
        },
        [close],
    );

    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    return {
        open,
        position,
        anchorRef,
        panelRef,
        close,
        openMenu,
        scheduleClose,
        handleKeyDown,
        hoverHandlers: { onMouseEnter: openMenu, onMouseLeave: scheduleClose },
        focusHandlers: { onFocus: openMenu, onBlur: handleBlur },
    };
}
